// PUSH-01 (VEK-77) — the browser side of Web Push: what this browser can do, and the subscription it
// holds. The service worker (public/sw.js) is registered only when push is turned on or looked up, never
// at app start, so nothing about page loading changes for someone who never enables push.
import { apiClient } from './client';
import type { RegisterPushSubscriptionRequest } from './types';

export type PushSupport = 'insecure' | 'ios-not-installed' | 'unsupported' | 'ok';

const SERVICE_WORKER_URL = '/sw.js';
const LOGOUT_UNREGISTER_TIMEOUT_MS = 3000;

function isIos(): boolean {
  const ua = navigator.userAgent;
  // iPadOS reports itself as a Mac; a Mac with touch points is an iPad.
  return /iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
}

function isInstalledApp(): boolean {
  const standalone = (navigator as Navigator & { standalone?: boolean }).standalone === true;
  return standalone || window.matchMedia('(display-mode: standalone)').matches;
}

/**
 * Checked in this order: a plain-HTTP origin exposes no service worker at all (the pilot), and an iPhone
 * outside its Home Screen app exposes no PushManager — each gets its own explanation rather than a
 * generic "unsupported".
 */
export function pushSupport(): PushSupport {
  if (!window.isSecureContext) return 'insecure';
  if (isIos() && !isInstalledApp()) return 'ios-not-installed';
  if (!('serviceWorker' in navigator) || !('PushManager' in window) || !('Notification' in window)) return 'unsupported';
  return 'ok';
}

/** The subscription this browser holds for Vektra, or null. Never prompts. */
export async function currentSubscription(): Promise<PushSubscription | null> {
  if (pushSupport() !== 'ok') return null;
  const registration = await navigator.serviceWorker.getRegistration('/');
  return registration ? registration.pushManager.getSubscription() : null;
}

function base64UrlToBytes(value: string): Uint8Array<ArrayBuffer> {
  const padded = value.replace(/-/g, '+').replace(/_/g, '/') + '='.repeat((4 - (value.length % 4)) % 4);
  const binary = atob(padded);
  const bytes = new Uint8Array(new ArrayBuffer(binary.length));
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

/**
 * Asks for notification permission — only ever called from the user's own press of *Enable* — then
 * registers the service worker and subscribes. Resolves to null when permission is not granted.
 */
export async function subscribeBrowser(publicKey: string): Promise<PushSubscription | null> {
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return null;
  await navigator.serviceWorker.register(SERVICE_WORKER_URL, { scope: '/' });
  const registration = await navigator.serviceWorker.ready;
  const existing = await registration.pushManager.getSubscription();
  if (existing) return existing;
  return registration.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: base64UrlToBytes(publicKey) });
}

export function toRegisterRequest(subscription: PushSubscription): RegisterPushSubscriptionRequest {
  const json = subscription.toJSON();
  return {
    endpoint: subscription.endpoint,
    keys: { p256dh: json.keys?.p256dh ?? '', auth: json.keys?.auth ?? '' }
  };
}

/**
 * Logout's part (spec: *Logout unregisters this device*): drop this browser's subscription on the server
 * and in the browser, so the next person signed in on this phone never sees the previous user's pushes.
 * Best effort and bounded — it never throws and never holds logout up for more than a few seconds.
 */
export async function unregisterOnLogout(): Promise<void> {
  const attempt = (async () => {
    const subscription = await currentSubscription();
    if (!subscription) return;
    try {
      await apiClient.delete<void>(`/push/subscriptions?endpoint=${encodeURIComponent(subscription.endpoint)}`);
    } finally {
      await subscription.unsubscribe();
    }
  })();
  const timeout = new Promise<void>((resolve) => setTimeout(resolve, LOGOUT_UNREGISTER_TIMEOUT_MS));
  await Promise.race([attempt.catch(() => undefined), timeout]);
}
